import React from 'react';
import { experienceIsRental } from '../../../../../../../shared/lib/reserve';
import { useEventCache } from '../context/EventCacheContext';
import { RESERVE_STEP } from '../views/Reserve';
import Icon from './Icon';

interface BreadcrumbProps {
  currentStep: number;
  steps: RESERVE_STEP[];
  onStepClick?: (index: number) => void;
}

export default function Breadcrumb(props: BreadcrumbProps) {
  const { currentStep, steps, onStepClick } = props;
  const { event } = useEventCache();

  const currentStepKey = steps[currentStep];
  const isConfirmation = currentStepKey === 'complete';
  const isCheckoutPhase = currentStepKey === 'cart' || currentStepKey === 'payment';
  const isRental = experienceIsRental(event?.eventTemplate?.reservables?.experienceType);

  const navClasses = ['flex text-sm text-secondary flex-row flex-wrap gap-y-2 sm:items-center'];

  const experienceCrumbs = [
    { label: 'All Experiences', step: 'list' as const, idx: steps.indexOf('grid') },
    {
      label: event?.eventTemplate.title ?? 'Experience',
      step: 'detailedBookable' as const,
      idx: steps.indexOf('event'),
    },
    { label: 'Date & Time', step: 'dateTime' as const, idx: steps.indexOf('date') },
    ...(steps.indexOf('reservationFields') !== -1
      ? [
          {
            label: 'Reservation',
            step: 'reservationFields' as const,
            idx: steps.indexOf('reservationFields'),
          },
        ]
      : []),
    {
      label: isRental ? 'Rental' : 'Ticket',
      step: 'details' as const,
      idx: steps.indexOf('ticketFields'),
    },
    ...(steps.indexOf('packages') !== -1
      ? [{ label: 'Packages', step: 'packages' as const, idx: steps.indexOf('packages') }]
      : []),
    ...(steps.indexOf('addons') !== -1
      ? [{ label: 'Addons', step: 'addons' as const, idx: steps.indexOf('addons') }]
      : []),
    { label: 'Checkout', step: 'cart' as const, idx: steps.indexOf('cart') },
  ].filter(c => c.idx !== -1);

  const checkoutCrumbs = [
    { label: 'All Experiences', step: 'list' as const, idx: steps.indexOf('grid') },
    { label: 'Cart Summary', step: 'cart' as const, idx: steps.indexOf('cart') },
    { label: 'Payment', step: 'payment' as const, idx: steps.indexOf('payment') },
  ].filter(c => c.idx !== -1);

  const crumbs = isCheckoutPhase ? checkoutCrumbs : experienceCrumbs;

  return (
    <nav aria-label="Breadcrumb" className={navClasses.join(' ')}>
      {isConfirmation ? (
        <span
          role={onStepClick ? 'button' : undefined}
          tabIndex={onStepClick ? 0 : -1}
          onClick={() => {
            const idx = steps.indexOf('grid');
            if (idx !== -1) onStepClick?.(idx);
          }}
          className={`outline-none whitespace-nowrap ${onStepClick ? 'cursor-pointer text-active' : ''}`}
          title="Back to All Experiences"
        >
          Back to All Experiences
        </span>
      ) : (
        crumbs.map((c, i) => {
          const isCurrent = c.idx === currentStep;
          const isPast = c.idx < currentStep;
          const isFuture = c.idx > currentStep;
          const isClickable = isPast;

          const classes = ['outline-none py-1 whitespace-nowrap'];

          if (isCurrent) classes.push('text-accent font-bold');
          if (isPast) classes.push('text-active');
          if (isFuture) classes.push('text-muted');
          if (isClickable) classes.push('cursor-pointer');

          return (
            <div key={`${c.label}-${c.idx}`} className="whitespace-nowrap">
              <span
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : -1}
                onClick={() => isClickable && onStepClick?.(c.idx)}
                className={classes.join(' ')}
                title={c.label}
              >
                {c.label}
              </span>

              {i < crumbs.length - 1 && (
                <Icon className="px-2 inline text-border" icon={['fass', 'chevron-right']} />
              )}
            </div>
          );
        })
      )}
    </nav>
  );
}
